import React from 'react';
import { Col, Container, Row, Card } from 'react-bootstrap';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { Srcset } from '@/components/Srcset/Srcset';
import { ContentPageLayout } from '@/components/Layouts/ContentPageLayout';
import { ActivityIndicator } from '@/components/ActivityIndicator/ActivityIndicator';
import { useIsRouterChanging } from '@/hooks/useIsRouterChanging';
import { useShowFilters } from '@/hooks/useShowFilters';
import { Pagination } from './components/BlogPagination';
import styles from './blog.module.scss';
import BlogFilters from './components/BlogFilters';
import blogService, { IBlogListItem, IBlogListRequest, IBlogListResponse } from './blogService';

type SectorName = 'Medical' | 'Food' | 'Facility' | 'Industry';

// Utility functions moved outside component for better performance
const getSectorClassName = (sectorName: string) => {
  const sectorStyles = {
    Medical: styles.medical,
    Food: styles.food,
    Facility: styles.facility,
    Industry: styles.industry
  };
  return sectorStyles[sectorName as SectorName] || styles.defaultStyle;
};

const getSectorText = (sectorName: string) => {
  const sectorStyles = {
    Medical: styles.medicalText,
    Food: styles.foodText,
    Facility: styles.facilityText,
    Industry: styles.industryText
  };
  return sectorStyles[sectorName as SectorName] || styles.defaultStyle;
};

// Helper function to build URL parameters
const buildUrlParams = (filters: IBlogListRequest, showFilters: boolean, page?: number): URLSearchParams => {
  const params = new URLSearchParams();

  // Ensure page is a valid positive number - prioritize the page parameter over filters.page
  const pageNumber = Math.max(1, page !== undefined ? page : filters.page);
  params.set('page', pageNumber.toString());

  // Handle sectors with validation
  if (Array.isArray(filters.sectors) && filters.sectors.length === 0) {
    params.set('sector', '');
  } else if (Array.isArray(filters.sectors)) {
    filters.sectors.filter(Boolean).forEach((sector) => params.append('sector', sector));
  }

  // Handle categories with validation
  if (Array.isArray(filters.categories)) {
    filters.categories.filter(Boolean).forEach((category) => params.append('category', category));
  }

  // Handle showFilters
  if (showFilters) {
    params.set('showFilters', 'true');
  }

  return params;
};

const getUrl = (path: string, newPage: number, request: IBlogListRequest, showFilters?: boolean): string => {
  const params = buildUrlParams(request, showFilters ?? false, newPage);
  return `${path}?${params.toString()}`;
};

interface BlogProps {
  currentFilters: IBlogListRequest;
  blogData: IBlogListResponse | null;
  error: string | null;
  initialShowFilters: boolean;
  page: number;
}

const Blog = ({ currentFilters, blogData, error, initialShowFilters, page = 1 }: BlogProps) => {
  const { t } = useTranslation('blog');
  const router = useRouter();
  const publicUrl = process.env.URL_WWW_EXTERNAL;

  const { items = [], totalCount = 0, perPage = 0, facets = [] } = blogData || {};

  // Derived values - simple computations don't need memoization
  const hasItems = (items?.length ?? 0) > 0;
  const hasFacets = (facets?.length ?? 0) > 0;
  const totalPages = Math.ceil(Number(totalCount) / perPage);
  const hasActiveFilters = currentFilters.sectors.length > 0 || currentFilters.categories.length > 0;

  const { showFilters, toggleShowFilters } = useShowFilters(initialShowFilters);
  const isLoading = useIsRouterChanging();

  const handleRemoveSector = (sector: string) => {
    const newSectors = currentFilters.sectors.filter((sec) => sec !== sector);
    const newFilters = { ...currentFilters, sectors: newSectors, page: 1 };
    const params = buildUrlParams(newFilters, showFilters);
    router.push(`${router.pathname}?${params.toString()}`);
  };

  const handleRemoveCategory = (category: string) => {
    const newCategories = currentFilters.categories.filter((cat) => cat !== category);
    const newFilters = { ...currentFilters, categories: newCategories };
    const params = buildUrlParams(newFilters, showFilters);
    router.push(`${router.pathname}?${params.toString()}`);
  };

  const handleFiltersUpdate = (newFilters: IBlogListRequest) => {
    const params = buildUrlParams(newFilters, showFilters);
    router.push(`${router.pathname}?${params.toString()}`);
  };

  return (
    <>
      {isLoading && <ActivityIndicator />}
      <Head>
        <title>{`${t('blog-title')} - Carel Lurvink B.V.${page > 1 ? ` | Pagina ${page}` : ''}`}</title>
        {page > 1 ? (
          <link rel="canonical" href={`${publicUrl}${router.pathname}?page=${page}`} key="canonical" />
        ) : (
          <link rel="canonical" href={`${publicUrl}${router.pathname}`} key="canonical" />
        )}
        <meta name="description" content={t('meta.desc')} />
      </Head>
      {error ? (
        <div>{error}</div>
      ) : (
        <>
          <Container>
            <div className="d-lg-flex justify-content-between mt-4 align-items-center">
              <h1>{t('blog-title')}</h1>
              <button onClick={toggleShowFilters} className={`mb-2 text-white p-2 h3 mt-2 ${styles.sectorBackground}`}>
                <span className="fa fa-filter mr-1" aria-hidden="true"></span>
                {t('filters.button')}
              </button>
            </div>
            {hasActiveFilters && (
              <div className={styles.FilterBadgeList}>
                <span className="text-nowrap mb-1 mr-2">{t('active.filters')}</span>

                {currentFilters.sectors.map((sector: string) => (
                  <div className={styles.FilterBadge} key={sector}>
                    <button
                      className="badge btn bg-theme-2 text-white mr-2"
                      role="button"
                      rel={sector}
                      onClick={() => handleRemoveSector(sector)}
                    >
                      <span className={styles.value}>{sector}</span>
                      <span className="fa fa-times ml-2"></span>
                    </button>
                  </div>
                ))}

                {currentFilters.categories.map((category: string) => (
                  <div className={styles.FilterBadge} key={category}>
                    <button
                      className="badge btn bg-theme-2 text-white mr-2"
                      role="button"
                      rel={category}
                      onClick={() => handleRemoveCategory(category)}
                    >
                      <span className={styles.value}>{category}</span>
                      <span className="fa fa-times ml-2"></span>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showFilters && hasFacets && (
              <BlogFilters
                facets={facets}
                request={currentFilters}
                setRequest={(newRequestOrUpdater) => {
                  const newFilters = typeof newRequestOrUpdater === 'function' ? newRequestOrUpdater(currentFilters) : newRequestOrUpdater;
                  handleFiltersUpdate(newFilters);
                }}
              />
            )}
            <Row className="mt-2">
              {hasItems && items[0] ? (
                <Col xs={12} lg={12} className={'mb-4 d-lg-flex'}>
                  <div className="w-100">
                    <Link href={items[0].url}>
                      <Srcset image={items[0].image} className={styles.cardImage} />
                    </Link>
                  </div>
                  <div className={`w-100 ${styles.firstBlog} d-flex flex-column align-items-center justify-content-center`}>
                    <div>
                      {items[0].sectors?.length > 0 && items[0].sectors[0] && (
                        <div className="my-3 ml-2 ">
                          <span className={`${getSectorText(items[0].sectors[0])} font-weight-bold bg-white p-2`}>
                            {items[0].sectors[0]}
                          </span>
                        </div>
                      )}
                      <Link href={items[0].url}>
                        <h2 className="m-2 h2 text-lg">{items[0].title}</h2>
                      </Link>
                      {items[0].subtitle && <p className="ml-2 text-md-lg">{items[0].subtitle}</p>}
                    </div>
                  </div>
                </Col>
              ) : (
                <div className="d-flex justify-content-center w-100">
                  <h2 className="h1 my-7">{t('blogs.not-found')}</h2>
                </div>
              )}

              {items.slice(1).map((blog: IBlogListItem, idx: number) => {
                if (!blog?.url || !blog?.title) {
                  return null; // Skip invalid blog items
                }

                return (
                  <Col xs={12} lg={6} className={'mb-4'} key={blog.url || idx}>
                    <Card border="0" className="rounded-0 h-100">
                      <Link href={blog.url}>
                        <Srcset image={blog.image} className={styles.cardImage} />
                      </Link>
                      <div>
                        {blog.sectors?.length > 0 && blog.sectors[0] && (
                          <div className="my-3 ml-2 ">
                            <span className={`${getSectorClassName(blog.sectors[0])} font-weight-bold text-white p-2`}>
                              {blog.sectors[0]}
                            </span>
                          </div>
                        )}

                        <Link href={blog.url}>
                          <h3 className="m-2">{blog.title}</h3>
                        </Link>
                        {blog.subtitle && <p className="ml-2">{blog.subtitle}</p>}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Container>
          <Pagination
            pageCount={totalPages}
            currentPage={page}
            getUrl={(newPage) => getUrl(router.pathname, newPage, currentFilters, showFilters)}
          />
        </>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps<BlogProps> = async (context: GetServerSidePropsContext) => {
  const page = context.query.page && Number(context.query.page) > 1 ? Number(context.query.page) : 1;
  let sectors: string[];

  if (context.query.sector === undefined) {
    // No sector parameter present - use cookie default
    const cookieSector = context.req.cookies['cl_sector'] ?? 'Facility';
    const sector = cookieSector === 'Food' ? 'Pharmacy & Food' : cookieSector;
    sectors = [sector];
  } else {
    // Sector parameter is present (could be empty string, single value, or array)
    // Handle explicitly empty case: when user unchecks all sectors
    if (context.query.sector === '' || (Array.isArray(context.query.sector) && context.query.sector.length === 0)) {
      sectors = [];
    } else {
      sectors = Array.isArray(context.query.sector) ? context.query.sector : [context.query.sector];
    }
  }

  const currentFilters: IBlogListRequest = {
    sectors,
    categories: Array.isArray(context.query.category) ? context.query.category : context.query.category ? [context.query.category] : [],
    page: page
  };

  // Determine initial showFilters state from URL parameter or fallback to having active filters
  const showFiltersParam = context.query.showFilters;
  const initialShowFilters = showFiltersParam === 'true';

  try {
    const blogData = await blogService.fetchBlogList(currentFilters);

    return {
      props: {
        currentFilters,
        blogData,
        error: null,
        initialShowFilters,
        page
      }
    };
  } catch (error) {
    return {
      props: {
        currentFilters,
        blogData: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
        initialShowFilters,
        page
      }
    };
  }
};

Blog.getLayout = function getLayout(page: React.ReactNode) {
  return <ContentPageLayout>{page}</ContentPageLayout>;
};

export default Blog;
